<template>
  <v-card>
    <v-card-title class="primaryDialogTitle">
      <span class="headline">{{ isNew ? 'Add Holiday' : 'Edit Holiday' }}</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <ValidationObserver ref="form" v-slot="{ invalid }">
        <v-container>
          <!-- Holiday Name -->
          <v-row>
            <v-col cols="12" md="6">
              <ValidationProvider v-slot="{ errors }" name="Holiday Name (EN)" rules="required">
                <FletrixTextField
                  v-model="formData.name.en"
                  :error="!!errors.length"
                  :error-messages="errors"
                  label="Holiday Name (EN)"
                  dense
                  placeholder="e.g., Chinese New Year"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider v-slot="{ errors }" name="Holiday Name (ZH-HK)" rules="required">
                <FletrixTextField
                  v-model="formData.name['zh-hk']"
                  :error="!!errors.length"
                  :error-messages="errors"
                  label="Holiday Name (ZH-HK)"
                  dense
                  placeholder="例如：農曆新年"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider v-slot="{ errors }" name="Holiday Name (ZH-CN)" rules="required">
                <FletrixTextField
                  v-model="formData.name['zh-cn']"
                  :error="!!errors.length"
                  :error-messages="errors"
                  label="Holiday Name (ZH-CN)"
                  dense
                  placeholder="例如：春节"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <!-- Date Range -->
          <v-row>
            <v-col cols="12" md="6">
              <ValidationProvider v-slot="{ errors }" name="Start Date" rules="required">
                <FletrixDatePicker
                  v-model="formData.time.start"
                  :error="!!errors.length"
                  :error-messages="errors"
                  label="Start Date"
                />
                <!-- <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formData.time.start"
                      :error="!!errors.length"
                      :error-messages="errors"
                      label="Start Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.time.start"
                    @input="startDateMenu = false"
                    :min="minDate"
                  ></v-date-picker>
                </v-menu> -->
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider v-slot="{ errors }" name="End Date" rules="required">
                <!-- <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formData.time.end"
                      :error="!!errors.length"
                      :error-messages="errors"
                      label="End Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.time.end"
                    @input="endDateMenu = false"
                    :min="formData.time.start || minDate"
                  ></v-date-picker>
                </v-menu> -->
                <FletrixDatePicker
                  v-model="formData.time.end"
                  :error="!!errors.length"
                  :error-messages="errors"
                  label="End Date"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <!-- Duration Display -->
          <v-row v-if="formData.time.start && formData.time.end">
            <v-col cols="12">
              <v-alert type="info" text dense> Duration: {{ getDuration() }} </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </ValidationObserver>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="loading" :disabled="!isValid" @click="save">
        {{ isNew ? 'Create' : 'Update' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import api from '@/api';
import { mapActions } from 'vuex';
import moment from 'moment';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { RESTFUL, holidayType, holidayReason } from '@/data/constants';
import { FletrixDatePicker, FletrixTextField } from '@/components/Atom';

export default {
  name: 'HolidayCard',
  components: {
    ValidationObserver,
    ValidationProvider,
    FletrixDatePicker,
    FletrixTextField,
  },
  props: {
    holidayId: {
      type: String,
      default: null,
    },
    holidayData: {
      type: Object,
      default: null,
    },
    clinicId: {
      type: String,
      required: true,
    },
    doctorId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      startDateMenu: false,
      endDateMenu: false,
      formData: {
        name: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        time: {
          start: null,
          end: null,
        },
        clinic: this.clinicId,
      },
    };
  },
  computed: {
    isNew() {
      return !this.holidayId;
    },
    isValid() {
      return (
        this.formData.name.en &&
        this.formData.name['zh-hk'] &&
        this.formData.name['zh-cn'] &&
        this.formData.time.start &&
        this.formData.time.end
      );
    },
    minDate() {
      return moment().format('YYYY-MM-DD');
    },
  },
  mounted() {
    if (this.holidayData) {
      this.loadHolidayData();
    }
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    loadHolidayData() {
      // Parse name if it's a string
      if (this.holidayData.name) {
        try {
          const name =
            typeof this.holidayData.name === 'string' && this.holidayData.name.startsWith('{')
              ? JSON.parse(this.holidayData.name)
              : this.holidayData.name;
          if (typeof name === 'object') {
            this.formData.name = { ...this.formData.name, ...name };
          } else {
            // If it's a plain string, set it as English
            this.formData.name.en = name;
          }
        } catch (e) {
          console.error('Error parsing name:', e);
          this.formData.name.en = this.holidayData.name;
        }
      }

      // Load time range
      if (this.holidayData.time) {
        this.formData.time = {
          start: this.holidayData.time.start
            ? moment(this.holidayData.time.start).format('YYYY-MM-DD')
            : null,
          end: this.holidayData.time.end
            ? moment(this.holidayData.time.end).format('YYYY-MM-DD')
            : null,
        };
      }
    },

    getDuration() {
      if (!this.formData.time.start || !this.formData.time.end) return '';
      const start = moment(this.formData.time.start);
      const end = moment(this.formData.time.end);
      const days = end.diff(start, 'days') + 1;
      return days === 1 ? '1 day' : `${days} days`;
    },

    async save() {
      const isValid =
        this.$refs.form && this.$refs.form.validate ? await this.$refs.form.validate() : false;
      if (!isValid) {
        return;
      }

      try {
        this.loading = true;

        const payload = {
          name: JSON.stringify(this.formData.name),
          time: {
            start: moment(this.formData.time.start).toISOString(),
            end: moment(this.formData.time.end).endOf('day').toISOString(),
          },
          clinic: this.clinicId,
          type: this.doctorId ? holidayType.doctor : holidayType.clinic,
          reason: holidayReason.scheduled,
        };

        // Add doctor if this is a doctor holiday
        if (this.doctorId) {
          payload.doctor = this.doctorId;
        }

        if (this.isNew) {
          await api.post(RESTFUL.holidays.list, payload);
          await this.updateAlertMessage({
            msg: 'Holiday created successfully',
            type: 'success',
            color: 'success',
          });
        } else {
          await api.patch(`${RESTFUL.holidays.list}/${this.holidayId}`, payload);
          await this.updateAlertMessage({
            msg: 'Holiday updated successfully',
            type: 'success',
            color: 'success',
          });
        }

        this.$emit('saved');
      } catch (error) {
        console.error('Error saving holiday:', error);
        await this.updateAlertMessage({
          msg: `Failed to ${this.isNew ? 'create' : 'update'} holiday`,
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped></style>
